import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../components/ui/accordion";
import CategoryPageShell from "../../../components/workspace/CategoryPageShell";
import { DriveLink } from "../../../components/workspace/HubText";
import ManualHandlingSafety from "../../../components/workspace/ManualHandlingSafety";
import { useHashAccordion } from "../../../hooks/useHashAccordion";
import { SAFETY_ITEMS } from "../../../data/proCarerCircleContent";

// These 4 forms already live in the Shift Kit — linked here (same
// underlying destination, not duplicated data) since they're also
// core Safety & Escalation reading.
const SHIFT_KIT_SAFETY_FORMS = [
  { id: "incident-report", title: "Incident Report", body: "Falls, medication errors, accidents and injuries — documented factually." },
  { id: "near-miss", title: "Near Miss Report", body: "Record risks you spotted and acted on before something happened." },
  { id: "safeguarding", title: "Safeguarding Concern", body: "Possible neglect, abuse or unexplained changes, sticking to facts." },
  { id: "emergency-escalation", title: "Emergency / Escalation Record", body: "A clear timeline for 999 calls, falls or urgent safeguarding." },
];

export default function SafetyEscalation() {
  const [openItem, setOpenItem] = useState("");
  useHashAccordion(SAFETY_ITEMS.map((i) => i.id), setOpenItem);

  return (
    <CategoryPageShell
      testId="pcc-category-safety-escalation"
      slug="safety-escalation"
      eyebrow="Safety & Escalation"
      title="Recognise risk. Respond safely. Document what happened."
      description="Manual handling safety, safeguarding escalation, and the shift forms you reach for when something goes wrong."
    >
      <ManualHandlingSafety />

      <section className="min-w-0" data-testid="safety-shift-forms">
        <h3 className="font-display text-xl font-semibold mb-1">When something happens on shift</h3>
        <p className="text-ink-muted text-sm mb-4">
          These forms live in the Shift Kit — linked here so you can find them from either place.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {SHIFT_KIT_SAFETY_FORMS.map((f) => (
            <Link
              key={f.id}
              to={`/workspace/pro-carer-circle/shift-kit#${f.id}`}
              className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-teal hover:shadow-md transition-all min-w-0"
              data-testid={`safety-shift-link-${f.id}`}
            >
              <div className="font-display font-semibold text-ink">{f.title}</div>
              <p className="mt-1 text-sm text-ink-muted leading-relaxed">{f.body}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-teal">
                Open in Shift Kit <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="min-w-0">
        <div className="mb-5 flex items-center gap-2 text-teal">
          <AlertTriangle className="w-4 h-4" strokeWidth={2} />
          <span className="text-sm font-semibold uppercase tracking-wide">Escalation guidance</span>
        </div>
        <Accordion type="single" collapsible value={openItem} onValueChange={setOpenItem} className="space-y-3" data-testid="safety-accordion">
          {SAFETY_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <AccordionItem key={item.id} value={item.id} id={item.id} className="rounded-2xl border border-slate-200 bg-white px-5 md:px-7 scroll-mt-24 min-w-0">
                <AccordionTrigger className="py-5 hover:no-underline">
                  <span className="flex items-center gap-3 text-left">
                    <Icon className="w-5 h-5 text-purple shrink-0" strokeWidth={1.75} />
                    <span className="flex flex-col">
                      <span className="font-display text-lg font-semibold">{item.title}</span>
                      <span className="text-sm text-ink-muted font-normal">{item.summary}</span>
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-6 pt-1 text-base leading-relaxed">
                  {item.body}
                  {item.download && (
                    <div className="mt-6">
                      <DriveLink href={item.download.href} label={item.download.label} variant="download" testId={`safety-dl-${item.id}`} />
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </section>
    </CategoryPageShell>
  );
}
